import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from '@tiptap/markdown';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyleKit } from '@tiptap/extension-text-style/text-style-kit';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { cn } from 'utils/cn';
import toast from 'react-hot-toast';
import ManualDocumentDocxUpload from 'components/ui/ManualDocumentDocxUpload';
import SpeedDialFab from 'components/ui/SpeedDialFab';
import RichEditorInstructionsPanel, {
  RichEditorKeyboardHintItems,
} from 'components/rich-text/RichEditorKeyboardHints';
import { replaceAllEditorContent } from '../extensions/fieldTokenEditorUtils';
import { useTemplateEditor } from '../context/TemplateEditorContext';
import FieldToken from '../extensions/FieldTokenNode';
import FieldTokenAutoDetect from '../extensions/FieldTokenAutoDetect';
import TabHandler from 'extensions/TabHandler';
import ClearEditorConfirmDialog from './ClearEditorConfirmDialog';
import '../editor.css';

const FONT_SIZE_OPTIONS = [
  { label: '10 pt', value: '10px' },
  { label: '11 pt', value: '11px' },
  { label: '12 pt', value: '12px' },
  { label: '14 pt', value: '14px' },
  { label: '16 pt', value: '16px' },
  { label: '18 pt', value: '18px' },
  { label: '20 pt', value: '20px' },
  { label: '24 pt', value: '24px' },
];

const ToolbarButton = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'inline-flex h-9 w-9 items-center justify-center rounded-md border border-transparent text-muted-foreground transition-colors',
      'hover:bg-muted hover:text-foreground',
      active && 'bg-muted text-foreground border-border',
      disabled && 'opacity-40 pointer-events-none'
    )}
  >
    {children}
  </button>
);

const ToolbarDivider = () => (
  <span className="mx-1 h-6 w-px shrink-0 bg-border self-center" aria-hidden />
);

function useEditorUiTick(editor) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!editor) return undefined;
    const bump = () => setTick((value) => value + 1);
    editor.on('transaction', bump);
    editor.on('selectionUpdate', bump);
    return () => {
      editor.off('transaction', bump);
      editor.off('selectionUpdate', bump);
    };
  }, [editor]);
}

function getFontSizeValue(editor) {
  const size = editor.getAttributes('textStyle')?.fontSize;
  if (!size) return '16px';
  const match = FONT_SIZE_OPTIONS.find((option) => option.value === size);
  return match?.value ?? size;
}

function useEditorShellMaxHeight(shellRef) {
  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell) return undefined;

    const apply = () => {
      const top = shell.getBoundingClientRect().top;
      const parent = shell.parentElement;
      const paddingBottom = parent
        ? parseFloat(getComputedStyle(parent).paddingBottom) || 0
        : 0;
      const available = window.innerHeight - top - paddingBottom;
      shell.style.maxHeight = `${Math.max(280, Math.floor(available))}px`;
    };

    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(shell);
    if (shell.parentElement) ro.observe(shell.parentElement);

    window.addEventListener('resize', apply);
    window.addEventListener('scroll', apply, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', apply);
      window.removeEventListener('scroll', apply);
    };
  }, [shellRef]);
}

function EditorToolbar({ editor }) {
  useEditorUiTick(editor);

  if (!editor) {
    return (
      <div className="template-editor-toolbar flex min-h-[48px] shrink-0 items-center gap-1 border-b border-border bg-muted/95 px-2 py-2">
        <span className="px-2 text-xs text-muted-foreground">Carregando editor…</span>
      </div>
    );
  }

  const fontSizeValue = getFontSizeValue(editor);

  return (
    <div className="template-editor-toolbar w-full max-w-full min-w-0 shrink-0 overflow-x-auto border-b border-border bg-muted/95 backdrop-blur-md shadow-sm">
      <div className="flex flex-wrap items-center gap-1 px-2 py-2 min-w-0 max-w-full">
        <select
          className="h-9 max-w-[110px] rounded-md border border-input bg-background px-2 text-xs"
          value={fontSizeValue}
          onChange={(e) => {
            const value = e.target.value;
            if (!value) editor.chain().focus().unsetFontSize().run();
            else editor.chain().focus().setFontSize(value).run();
          }}
          title="Tamanho da fonte"
          aria-label="Tamanho da fonte"
        >
          {FONT_SIZE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <ToolbarDivider />

        <ToolbarButton
          title="Negrito"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
        >
          <Icon name="Bold" size={18} />
        </ToolbarButton>
        <ToolbarButton
          title="Itálico"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
        >
          <Icon name="Italic" size={18} />
        </ToolbarButton>
        <ToolbarButton
          title="Sublinhado"
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <Icon name="Underline" size={18} />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          title="Alinhar à esquerda"
          active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
        >
          <Icon name="AlignLeft" size={18} />
        </ToolbarButton>
        <ToolbarButton
          title="Centralizar"
          active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
        >
          <Icon name="AlignCenter" size={18} />
        </ToolbarButton>
        <ToolbarButton
          title="Alinhar à direita"
          active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
        >
          <Icon name="AlignRight" size={18} />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          title="Lista com marcadores"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <Icon name="List" size={18} />
        </ToolbarButton>
        <ToolbarButton
          title="Lista numerada"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <Icon name="ListOrdered" size={18} />
        </ToolbarButton>
      </div>
    </div>
  );
}

const EditorPanel = () => {
  const {
    registerEditor,
    ensureFieldRef,
    syncFieldsFromEditorContent,
    clearAllFields,
    saveTemplate,
    saving,
    hasSavedSuccessfully,
    templateId,
    focusFieldInPanel,
    initialContent,
    editorMountKey,
  } = useTemplateEditor();
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const shellRef = useRef(null);
  const docxUploadRef = useRef(null);
  const focusFieldInPanelRef = useRef(focusFieldInPanel);
  focusFieldInPanelRef.current = focusFieldInPanel;
  useEditorShellMaxHeight(shellRef);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Markdown,
      Underline,
      TextStyleKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder: 'Comece a elaborar seu modelo aqui…',
      }),
      FieldToken,
      FieldTokenAutoDetect.configure({
        getEnsureField: () => ensureFieldRef.current,
      }),
      TabHandler,
    ],
    [ensureFieldRef]
  );

  const editor = useEditor(
    {
      extensions,
      content: initialContent ?? '<p></p>',
      editorProps: {
        attributes: {
          class: 'template-editor-prosemirror focus:outline-none',
        },
        handleClickOn(_view, _pos, node) {
          if (node.type.name === 'fieldToken' && node.attrs.uuid) {
            focusFieldInPanelRef.current?.(node.attrs.uuid);
          }
          return false;
        },
      },
    },
    [editorMountKey, extensions]
  );

  useEffect(() => {
    if (!editor) return undefined;

    const syncActiveFieldFromSelection = () => {
      const { selection } = editor.state;

      if (selection.node?.type?.name === 'fieldToken' && selection.node.attrs.uuid) {
        focusFieldInPanelRef.current?.(selection.node.attrs.uuid);
        return;
      }

      const { $from } = selection;
      if ($from.nodeAfter?.type?.name === 'fieldToken' && $from.nodeAfter.attrs.uuid) {
        focusFieldInPanelRef.current?.($from.nodeAfter.attrs.uuid);
        return;
      }

      if ($from.nodeBefore?.type?.name === 'fieldToken' && $from.nodeBefore.attrs.uuid) {
        focusFieldInPanelRef.current?.($from.nodeBefore.attrs.uuid);
      }
    };

    editor.on('selectionUpdate', syncActiveFieldFromSelection);
    return () => {
      if (!editor.isDestroyed) {
        editor.off('selectionUpdate', syncActiveFieldFromSelection);
      }
    };
  }, [editor]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      registerEditor(null);
      return undefined;
    }
    registerEditor(editor);
    return () => registerEditor(null);
  }, [editor, registerEditor]);

  const handleDocxImportSuccess = ({ replaced = false } = {}) => {
    window.setTimeout(() => {
      const { keys, replaced: tokensReplaced } = syncFieldsFromEditorContent(true);
      if (keys.length > 0) {
        toast.success(
          `${keys.length} campo(s) detectado(s) no documento${tokensReplaced ? ` (${tokensReplaced} token(s) convertido(s))` : ''}.`
        );
      }
    }, 0);
  };

  const handleReturnToList = () => {
    const confirmed = window.confirm('Lembre-se de salvar seu modelo antes de sair.');
    if (confirmed) {
      navigate('/user-templates');
    }
  };

  const handleGenerateDocument = async () => {
    if (!hasSavedSuccessfully || !editor || importing || saving) return;

    const result = await saveTemplate();
    if (!result?.success) return;

    const id = templateId;
    if (!id) {
      toast.error('Não foi possível gerar documento: modelo sem identificador.');
      return;
    }

    navigate(
      `/contract-generator?user_template_id=${encodeURIComponent(String(id))}`
    );
  };

  const handleConfirmClearEditor = () => {
    if (!editor || importing || saving) return;
    clearAllFields();
    replaceAllEditorContent(editor, '<p></p>');
    setClearConfirmOpen(false);
    toast.success('Editor limpo.');
  };

  const openDocxImport = () => {
    docxUploadRef.current?.open();
  };

  const editorActionsDisabled = !editor || importing || saving;

  const speedDialActions = [
    {
      id: 'clear',
      label: 'Limpar Editor',
      iconName: 'Eraser',
      disabled: editorActionsDisabled,
      onClick: () => setClearConfirmOpen(true),
    },
    {
      id: 'import-docx',
      label: importing ? 'Importando…' : 'Importar Docx',
      iconName: 'Upload',
      disabled: !editor || importing,
      onClick: openDocxImport,
    },
    {
      id: 'generate',
      label: 'Gerar documento',
      iconName: 'FileText',
      disabled: editorActionsDisabled || !hasSavedSuccessfully,
      onClick: handleGenerateDocument,
    },
    {
      id: 'save',
      label: saving ? 'Salvando…' : 'Salvar',
      iconName: 'Save',
      variant: 'primary',
      disabled: editorActionsDisabled,
      onClick: () => saveTemplate(),
    },
  ];

  return (
    <section className="flex h-full min-h-0 w-full flex-col">
      <div className="mb-3 flex shrink-0 items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          iconName="ArrowLeft"
          onClick={handleReturnToList}
        >
          Retornar a Lista
        </Button>

        <div className="hidden md:flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            iconName="Eraser"
            disabled={editorActionsDisabled}
            onClick={() => setClearConfirmOpen(true)}
          >
            Limpar Editor
          </Button>
          <Button
            type="button"
            variant="outline"
            iconName="Upload"
            disabled={!editor || importing}
            onClick={openDocxImport}
          >
            {importing ? 'Importando…' : 'Importar Docx'}
          </Button>
          <Button
            type="button"
            variant="outline"
            iconName="FileText"
            disabled={editorActionsDisabled || !hasSavedSuccessfully}
            onClick={handleGenerateDocument}
          >
            Gerar documento
          </Button>
          <Button
            type="button"
            variant="default"
            iconName="Save"
            disabled={editorActionsDisabled}
            onClick={() => saveTemplate()}
          >
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </div>

      <ManualDocumentDocxUpload
        ref={docxUploadRef}
        editor={editor}
        disabled={!editor}
        uploading={importing}
        onUploadingChange={setImporting}
        label="Importar Docx"
        iconName="Upload"
        confirmReplace
        allowFieldTokenDeletionOnReplace
        replaceAllEditorContent={replaceAllEditorContent}
        onBeforeReplace={clearAllFields}
        onSuccess={handleDocxImportSuccess}
        hideButton
      />

      <SpeedDialFab ariaLabel="Ações do modelo" actions={speedDialActions} />

      <ClearEditorConfirmDialog
        open={clearConfirmOpen}
        onCancel={() => setClearConfirmOpen(false)}
        onConfirm={handleConfirmClearEditor}
      />

      <RichEditorInstructionsPanel className="mb-3 shrink-0">
        <RichEditorKeyboardHintItems />
      </RichEditorInstructionsPanel>

      <div
        ref={shellRef}
        className="template-rich-editor-shell flex min-h-[min(52vh,520px)] w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-elevation-sm sm:min-h-[min(60vh,640px)] sm:rounded-xl"
      >
        <EditorToolbar editor={editor} />
        <div className="template-editor-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="template-editor-canvas">
            <EditorContent editor={editor} className="template-editor-paper" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default EditorPanel;
